/**
 * CreateLead — full-page "New Lead" form rendered 1:1 from
 *   /Users/gudupuramesh/Documents/design ui/anti Interior io ui web/create-lead.html
 *
 * Verbatim:
 *   - Sticky `.projects-header` with Back arrow + "New Lead" + Cancel/Save
 *   - Two `.card` sections (24px padding) — Lead Details + Management
 *   - Plain HTML <input>/<select>/<textarea> with the folder's inline
 *     styles, NOT Mantine controls. Matches iio.css typography 1:1.
 *
 * Real data path: `createLead` (web SDK direct write to top-level
 * `leads` collection). Same helper the old modal used.
 *
 * Not used: NewLeadModal.tsx (kept in tree as legacy; this page is the
 * primary entry point now, opened from the "+ New Lead" button on the
 * Leads page).
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { canCRMWrite } from '@shared/perms';

import { useAuth } from '@/auth/AuthProvider';
import { PagePlaceholder } from '@/components/PagePlaceholder';
import { createLead } from '@/lib/leads';
import { useCurrentOrgRole } from '@/lib/useCurrentOrgRole';
import { useFullOrganization } from '@/lib/useFullOrganization';
import {
  BUDGET_RANGE_FORM_OPTIONS,
  POSSESSION_FORM_OPTIONS,
  PRIORITY_FORM_OPTIONS,
  PROJECT_TYPE_FORM_OPTIONS,
  SOURCE_FORM_OPTIONS,
  STATUS_FORM_OPTIONS,
  type BudgetRange,
  type LeadPriority,
  type LeadSource,
  type LeadStatus,
  type PossessionStatus,
  type ProjectType,
} from '@/lib/useLeads';

// ───── Inline styles — copied verbatim from create-lead.html ─────

const STYLE_INPUT: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid var(--border)',
  borderRadius: 6,
  background: '#fff',
  fontSize: 13,
  color: 'var(--text-primary)',
  fontFamily: 'inherit',
};

const STYLE_LABEL: React.CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--text-tertiary)',
  marginBottom: 6,
};

const STYLE_REQUIRED: React.CSSProperties = { color: 'var(--critical)' };

const STYLE_SECTION_TITLE: React.CSSProperties = {
  fontSize: 16,
  fontWeight: 600,
  margin: 0,
  marginBottom: 20,
  paddingBottom: 12,
  borderBottom: '1px solid var(--divider)',
  color: 'var(--text-primary)',
};

const STYLE_HEADER_STICKY: React.CSSProperties = {
  position: 'sticky',
  top: 0,
  background: 'var(--page-bg)',
  zIndex: 10,
  paddingBottom: 16,
  borderBottom: '1px solid var(--border)',
  marginBottom: 24,
};

const STYLE_GRID_1_1: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 16,
};

const STYLE_FULL_WIDTH: React.CSSProperties = { gridColumn: '1 / -1' };

// ───── Page ─────

export function CreateLeadPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { org } = useFullOrganization();
  const orgId = org?.id ?? null;
  const { role: orgRole } = useCurrentOrgRole();
  const canWrite = canCRMWrite(orgRole);

  // Section 1 — Lead Details
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [possessionStatus, setPossessionStatus] = useState<PossessionStatus | ''>('');
  const [location, setLocation] = useState('');
  const [projectType, setProjectType] = useState<ProjectType | ''>('');
  const [budgetRange, setBudgetRange] = useState<BudgetRange | ''>('');
  const [source, setSource] = useState<LeadSource | ''>('');

  // Section 2 — Management
  const [status, setStatus] = useState<LeadStatus>('new');
  const [priority, setPriority] = useState<LeadPriority>('medium');
  const [followUpAt, setFollowUpAt] = useState(''); // yyyy-mm-dd
  const [assignedTo, setAssignedTo] = useState('');
  const [notes, setNotes] = useState('');

  // UI state
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCancel = () => navigate('/leads');

  const handleSave = async () => {
    setError(null);
    if (!orgId) return setError('No organisation in scope.');
    if (!user) return setError('You must be signed in.');
    const tName = name.trim();
    if (tName.length < 2) return setError('Lead name must be at least 2 characters.');

    setBusy(true);
    try {
      const id = await createLead({
        orgId,
        name: tName,
        phone: phone.trim(),
        source: source || 'other',
        status,
        priority,
        createdBy: user.uid,
        email: email.trim() || undefined,
        possessionStatus: possessionStatus || undefined,
        projectType: projectType || undefined,
        budgetRange: budgetRange || undefined,
        location: location.trim() || undefined,
        followUpAt: followUpAt ? new Date(`${followUpAt}T00:00:00`) : undefined,
        assignedTo: assignedTo.trim() || undefined,
        notes: notes.trim() || undefined,
      });
      navigate(`/leads/${id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create lead.');
      setBusy(false);
    }
  };

  // Role gate — non-CRM-write roles see a placeholder instead of the
  // editable form they could never save. Mirrors ProjectEdit.tsx
  // pattern. Wait for orgRole to resolve before gating so we don't
  // flash the placeholder during initial claims hydration.
  if (orgRole && !canWrite) {
    return (
      <PagePlaceholder
        title="Access denied"
        body="Only Super Admin, Admin, or Manager can create leads. Ask one of them to add this lead for you."
      />
    );
  }

  return (
    <div className="page active" id="page-create-lead" style={{ display: 'block' }}>
      {/* ─── Sticky header ─── */}
      <div className="projects-header" style={STYLE_HEADER_STICKY}>
        <div className="projects-title-row">
          <button
            className="btn-ghost"
            style={{ padding: '4px 8px', marginLeft: -8 }}
            type="button"
            onClick={handleCancel}
            disabled={busy}
            aria-label="Back to leads"
          >
            <i className="ph ph-arrow-left" />
          </button>
          <h1>New Lead</h1>
        </div>
        <div className="projects-actions">
          <button
            className="btn-secondary"
            type="button"
            onClick={handleCancel}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            className="btn-primary"
            type="button"
            onClick={handleSave}
            disabled={busy || !orgId}
          >
            <i className="ph-bold ph-check" /> Save Lead
          </button>
        </div>
      </div>

      {error ? (
        <div
          className="card"
          style={{
            padding: 12,
            marginBottom: 16,
            borderColor: 'var(--critical)',
            color: 'var(--critical-text)',
            background: 'var(--critical-bg)',
            maxWidth: 800,
          }}
        >
          <i className="ph ph-warning-circle" style={{ marginRight: 6 }} />
          {error}
        </div>
      ) : null}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSave();
        }}
        style={{
          maxWidth: 800,
          display: 'flex',
          flexDirection: 'column',
          gap: 24,
          marginBottom: 60,
        }}
      >
        {/* ─── Section 1: Lead Details ─── */}
        <div className="card" style={{ padding: 24 }}>
          <h2 style={STYLE_SECTION_TITLE}>Lead Details</h2>
          <div className="responsive-grid-1-1" style={STYLE_GRID_1_1}>
            <div style={STYLE_FULL_WIDTH}>
              <label style={STYLE_LABEL}>
                Name <span style={STYLE_REQUIRED}>*</span>
              </label>
              <input
                type="text"
                placeholder="e.g. Amitabh Sharma"
                style={STYLE_INPUT}
                value={name}
                onChange={(e) => setName(e.currentTarget.value)}
                disabled={busy}
                required
                maxLength={80}
              />
            </div>

            <div>
              <label style={STYLE_LABEL}>Phone</label>
              <input
                type="tel"
                placeholder="+91 99999 99999"
                style={STYLE_INPUT}
                value={phone}
                onChange={(e) => setPhone(e.currentTarget.value)}
                disabled={busy}
              />
            </div>
            <div>
              <label style={STYLE_LABEL}>Email</label>
              <input
                type="email"
                placeholder="client@example.com"
                style={STYLE_INPUT}
                value={email}
                onChange={(e) => setEmail(e.currentTarget.value)}
                disabled={busy}
              />
            </div>

            <div>
              <label style={STYLE_LABEL}>Possession Status</label>
              <select
                style={STYLE_INPUT}
                value={possessionStatus}
                onChange={(e) => setPossessionStatus(e.currentTarget.value as PossessionStatus | '')}
                disabled={busy}
              >
                <option value="" disabled>Select status</option>
                {POSSESSION_FORM_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={STYLE_LABEL}>Location</label>
              <input
                type="text"
                placeholder="e.g. HSR Layout, Sector 2"
                style={STYLE_INPUT}
                value={location}
                onChange={(e) => setLocation(e.currentTarget.value)}
                disabled={busy}
              />
            </div>

            <div>
              <label style={STYLE_LABEL}>Project Type</label>
              <select
                style={STYLE_INPUT}
                value={projectType}
                onChange={(e) => setProjectType(e.currentTarget.value as ProjectType | '')}
                disabled={busy}
              >
                <option value="" disabled>Select project type</option>
                {PROJECT_TYPE_FORM_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={STYLE_LABEL}>Budget</label>
              <select
                style={STYLE_INPUT}
                value={budgetRange}
                onChange={(e) => setBudgetRange(e.currentTarget.value as BudgetRange | '')}
                disabled={busy}
              >
                <option value="" disabled>Select budget range</option>
                {BUDGET_RANGE_FORM_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            <div style={STYLE_FULL_WIDTH}>
              <label style={STYLE_LABEL}>Source</label>
              <select
                style={STYLE_INPUT}
                value={source}
                onChange={(e) => setSource(e.currentTarget.value as LeadSource | '')}
                disabled={busy}
              >
                <option value="" disabled>Select source</option>
                {SOURCE_FORM_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* ─── Section 2: Management & Tracking ─── */}
        <div className="card" style={{ padding: 24 }}>
          <h2 style={STYLE_SECTION_TITLE}>Management</h2>
          <div className="responsive-grid-1-1" style={STYLE_GRID_1_1}>
            <div>
              <label style={STYLE_LABEL}>Status</label>
              <select
                style={STYLE_INPUT}
                value={status}
                onChange={(e) => setStatus(e.currentTarget.value as LeadStatus)}
                disabled={busy}
              >
                {STATUS_FORM_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={STYLE_LABEL}>Priority</label>
              <select
                style={STYLE_INPUT}
                value={priority}
                onChange={(e) => setPriority(e.currentTarget.value as LeadPriority)}
                disabled={busy}
              >
                {PRIORITY_FORM_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={STYLE_LABEL}>Follow-up Date</label>
              <input
                type="date"
                style={STYLE_INPUT}
                value={followUpAt}
                onChange={(e) => setFollowUpAt(e.currentTarget.value)}
                disabled={busy}
              />
            </div>
            <div>
              <label style={STYLE_LABEL}>Assigned To</label>
              <input
                type="text"
                placeholder="Team member name"
                style={STYLE_INPUT}
                value={assignedTo}
                onChange={(e) => setAssignedTo(e.currentTarget.value)}
                disabled={busy}
              />
            </div>

            <div style={{ ...STYLE_FULL_WIDTH, marginTop: 8 }}>
              <label style={STYLE_LABEL}>Note</label>
              <textarea
                placeholder="Client is looking for a modern minimal design..."
                style={{
                  ...STYLE_INPUT,
                  minHeight: 100,
                  resize: 'vertical',
                }}
                value={notes}
                onChange={(e) => setNotes(e.currentTarget.value)}
                disabled={busy}
              />
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
